import { useCallback } from 'react';
import { routes } from '~/constants';
import { useIoContext, useOverlayContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { sendErrReport } from '~/infra/utils';
import { androidCallbackHost, ANDROID_SCHEME_CALLBACK_PATH, parseOAuthCallback } from '~/oauth/oauth-callback-listener';
import { makeOAuthStateStorage } from './oauthStateStorage';
const CALLBACK_PATH_BY_PROVIDER = {
    qbo: '/qbo-integration/oauth-callback',
    xero: '/xero-integration/oauth-callback'
};
export function useNativeOAuthSignIn(provider) {
    const { browser: { changeLocation, scrubUrlFragment }, native: { isNative, androidCallbackScheme, systemBrowser }, cookies, localStorage } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const providerErrorMessage = provider === 'qbo' ? commonTexts.toasts.failedToContinueWithIntuit() : commonTexts.toasts.failedToContinueWithXero();
    const showProviderErrorToast = useCallback(()=>{
        showErrorToast(providerErrorMessage, "native-oauth-".concat(provider, "-error-toast"));
    }, [
        provider,
        providerErrorMessage
    ]);
    const handleCallbackResult = useCallback(async (result)=>{
        const oauthStateStorage = makeOAuthStateStorage(localStorage);
        if (result.kind === 'ignored') {
            oauthStateStorage.clearPendingState(provider);
            sendErrReport(new Error("native-oauth-callback-ignored: ".concat(provider)));
            showProviderErrorToast();
            return;
        }
        if (result.kind === 'error') {
            oauthStateStorage.clearPendingState(result.provider);
            sendErrReport(new Error("native-oauth-callback-error: ".concat(result.error)));
            showProviderErrorToast();
            return;
        }
        const expectedState = oauthStateStorage.readPendingState(result.provider);
        if (expectedState == null || expectedState !== result.state) {
            oauthStateStorage.clearPendingState(result.provider);
            sendErrReport(new Error("native-oauth-state-mismatch: ".concat(result.provider)));
            showProviderErrorToast();
            return;
        }
        try {
            cookies.setAuthTokenForNative(result.authToken);
        } catch (err) {
            oauthStateStorage.clearPendingState(result.provider);
            sendErrReport(err);
            showProviderErrorToast();
            return;
        }
        oauthStateStorage.clearPendingState(result.provider);
        scrubUrlFragment();
        changeLocation(routes.home);
    }, [
        provider,
        showProviderErrorToast
    ]);
    const dispatchOAuth = useCallback(async (authUrl)=>{
        if (!isNative) {
            changeLocation(authUrl);
            return;
        }
        const oauthStateStorage = makeOAuthStateStorage(localStorage);
        const isAndroidCallback = androidCallbackScheme != null;
        let backendState;
        let callbackHost;
        try {
            backendState = new URL(authUrl).searchParams.get('state');
            callbackHost = isAndroidCallback ? androidCallbackHost(provider) : new URL(__BASE_URL__).host;
        } catch (err) {
            sendErrReport(err);
            showProviderErrorToast();
            return;
        }
        if (backendState == null) {
            sendErrReport(new Error("native-oauth-no-state-in-intuit-url: ".concat(provider)));
            showProviderErrorToast();
            return;
        }
        oauthStateStorage.persistPendingState(provider, backendState);
        const callbackPath = isAndroidCallback ? ANDROID_SCHEME_CALLBACK_PATH : CALLBACK_PATH_BY_PROVIDER[provider];
        try {
            const result = await systemBrowser.runOAuthRedirectFlow(authUrl, callbackHost, callbackPath);
            await handleCallbackResult(parseOAuthCallback(result));
        } catch (err) {
            oauthStateStorage.clearPendingState(provider);
            const code = err === null || err === void 0 ? void 0 : err.code;
            if (code !== 'USER_CANCELED') {
                sendErrReport(err);
                showProviderErrorToast();
            }
        }
    }, [
        provider,
        handleCallbackResult,
        showProviderErrorToast
    ]);
    return {
        isNative,
        androidCallbackScheme,
        dispatchOAuth
    };
}
